/**
 * Calcul pur (sans DOM) de la distance entre deux points et du tri par
 * distance des magasins, pour la carte de résultat (chantier
 * store-locator-revendeurs, point 4, critères 4-5, scénarios 9-12a).
 *
 * Fichier volontairement séparé du rendu DOM (`StoreCardMarkup.js`), sur le
 * même principe que `StoreOpeningLogic.js` (point 3) : testable directement
 * en Node, sans navigateur ni build du thème.
 *
 * Invariant : la « priorité d'affichage » n'est plus utilisée pour classer
 * cette liste (décision du 2026-09-23, critère 5) — seule la distance compte
 * À L'INTÉRIEUR de chaque groupe de type de magasin. Depuis le chantier
 * store-locator-ordre-types (2026-10-22), la liste est d'abord regroupée par
 * type (`sortItemsByTypeThenDistance`, `storeTypeRank`).
 */

const EARTH_RADIUS_KM = 6371;

/**
 * Distance à vol d'oiseau entre deux points (formule de Haversine).
 * @param  {{lat: number, lng: number}} a
 * @param  {{lat: number, lng: number}} b
 * @returns {number} Distance en kilomètres.
 */
export function haversineDistanceKm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Formate une distance en kilomètres pour l'affichage (« 850 m », « 3,2 km »).
 * @param  {number} km
 * @returns {string}
 */
export function formatDistance(km) {
  if (!Number.isFinite(km)) {
    return '';
  }
  if (km < 1) {
    return `${Math.max(0, Math.round(km * 1000))} m`;
  }
  const rounded = Math.round(km * 10) / 10;
  return `${rounded.toFixed(1).replace('.', ',')} km`;
}

/**
 * Trie une liste de magasins par distance croissante à un point de référence
 * (point de recherche, ou centre de la carte à défaut — critère 5, scénario
 * 12a). Chaque élément reçoit sa distance calculée dans `__distanceKm`
 * (kilomètres, non arrondie) pour un affichage éventuel par l'appelant.
 * @param  {Array<{lat: number, lng: number}>} items
 * @param  {{lat: number, lng: number}} referencePoint
 * @returns {Array}
 */
export function sortItemsByDistance(items, referencePoint) {
  return items
    .map((item) => ({
      ...item,
      __distanceKm: haversineDistanceKm(referencePoint, { lat: item.lat, lng: item.lng }),
    }))
    .sort((a, b) => a.__distanceKm - b.__distanceKm);
}

/**
 * Rang d'un magasin dans la liste (chantier store-locator-ordre-types) : 0 =
 * Concept Store, 1 = Corner Store, 2 = tout le reste (« revendeur », sans
 * catégorie, type inconnu — ces magasins restent mêlés, triés par distance).
 * Le type est le slug de la taxonomie `type` porté par `item.type` (chaîne
 * vide si le magasin n'a pas de catégorie).
 * @param  {{type?: string}} item
 * @returns {0|1|2}
 */
export function storeTypeRank(item) {
  if (item?.type === 'concept-store') {
    return 0;
  }
  if (item?.type === 'corner-store') {
    return 1;
  }
  return 2;
}

/**
 * Ordre de la liste « Nos revendeurs » : Concept Store, puis Corner Store, puis
 * le reste ; à rang égal, exactement l'ordre de `sortItemsByDistance` (distance
 * croissante au point de référence, égalités dans l'ordre d'origine — le tri
 * natif est stable). `__distanceKm` est attaché comme pour `sortItemsByDistance`.
 * @param  {Array<{lat: number, lng: number, type?: string}>} items
 * @param  {{lat: number, lng: number}} referencePoint
 * @returns {Array}
 */
export function sortItemsByTypeThenDistance(items, referencePoint) {
  return sortItemsByDistance(items, referencePoint)
    .sort((a, b) => storeTypeRank(a) - storeTypeRank(b));
}
